import React from 'react';
import {Button, Popover, PopoverBody, PopoverHeader} from 'reactstrap';
import classnames from '@src/helpers/classnames';
import Translate from '@src/components/Translate/Translate';
import {ConfirmableButtonProps, ConfirmableButtonState} from '@src/@types/ConfirmableButton';

const groups: Record<string, {
    id: string,
    close: () => void,
}[]> = {};

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

export default class ConfirmableButton<
        Props extends ConfirmableButtonProps = ConfirmableButtonProps,
        State extends ConfirmableButtonState = ConfirmableButtonState
> extends React.Component<Props, State> {
    protected static GLOBAL_GROUP_IDENTIFIER = 'global';

    state: State = {
        isOpen: false
    } as State;

    protected groupItemIdentifier!: string;
    protected buttonRef!: React.RefObject<HTMLButtonElement>;
    protected onTriggerClickTimer?: ReturnType<typeof setTimeout>;

    constructor(props: Props) {
        super(props);

        this.groupItemIdentifier = String(Date.now() + Math.random());
        this.buttonRef = React.createRef<HTMLButtonElement>();

        this.onDocumentClicked = this.onDocumentClicked.bind(this);
        this.onTriggerClicked = this.onTriggerClicked.bind(this);
        this.onConfirmButtonClicked = this.onConfirmButtonClicked.bind(this);
        this.onCancelButtonClicked = this.onCancelButtonClicked.bind(this);
    }

    componentDidMount() {
        document.addEventListener('click', this.onDocumentClicked);

        this.pushPopoverToGroup();
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.onDocumentClicked);

        this.removePopoverFromGroup();
    }

    render() {
        const {
            children,
            buttonElement: ButtonElement = Button,
            callableButtonElement,
            buttonLabel,
            popoverTitle = 'ConfirmableButton.title',
            containerClassName,
            popoverClassName,
            popoverProps = {},
            popoverContainer,
            popoverBody,
            popoverBodyProps = {},
            popoverPlacement,
            confirmButtonLabel = 'ConfirmableButton.confirm',
            confirmButtonProps = {},
            cancelButtonLabel = 'ConfirmableButton.cancel',
            cancelButtonProps = {},
            onlyOnePopoverCanBeOpened,// eslint-disable-line
            groupIdentifier,// eslint-disable-line
            onClick,// eslint-disable-line
            onCancel,// eslint-disable-line
            bsSize = 'sm',
            ...props
        } = this.props;

        const buttonContent = children ?? (buttonLabel ? <Translate message={buttonLabel}/> : null);

        return <>
            {callableButtonElement ? callableButtonElement({
                ...props,
                onClick: this.onTriggerClicked,
                innerRef: this.buttonRef,
                children: buttonContent,
            }) : (
                    <ButtonElement
                            {...props}
                            onClick={this.onTriggerClicked}
                            innerRef={this.buttonRef}
                    >
                        {buttonContent}
                    </ButtonElement>
            )}

            <Popover
                    target={this.buttonRef}
                    isOpen={this.state.isOpen}
                    delay={0}
                    fade={false}
                    onClick={stopPropagation}
                    className={containerClassName}
                    popperClassName={popoverClassName}
                    placement={popoverPlacement}
                    container={popoverContainer}
                    {...popoverProps}
            >
                {popoverTitle ? <Translate message={popoverTitle} tag={PopoverHeader}/> : null}

                <PopoverBody {...popoverBodyProps}>
                    {typeof popoverBody === 'string' ? <Translate message={popoverBody}/> : popoverBody}

                    <div className={classnames({'mt-3': popoverBody})}>
                        <Button
                                type="button"
                                size={bsSize}
                                color="danger"
                                {...confirmButtonProps}
                                onClick={this.onConfirmButtonClicked}
                        >
                            <Translate message={confirmButtonLabel}/>
                        </Button>
                        <Button
                                type="button"
                                size={bsSize}
                                color="success"
                                {...cancelButtonProps}
                                onClick={this.onCancelButtonClicked}
                                className={classnames('ms-2', cancelButtonProps.className)}
                        >
                            <Translate message={cancelButtonLabel}/>
                        </Button>
                    </div>
                </PopoverBody>
            </Popover>
        </>;
    }

    protected toggle(state: boolean | null = null, callback?: () => void) {
        state = typeof state === 'boolean' ? state : !this.state.isOpen;

        if (state === this.state.isOpen) {
            if (callback) {
                callback();
            }

            return;
        }

        this.setState({isOpen: state}, () => {
            if (state) {
                this.closeAnotherPopovers();
            }

            if (callback) {
                callback();
            }
        });
    }

    protected onTriggerClicked(e: React.MouseEvent): void {
        e.stopPropagation();

        if (this.onTriggerClickTimer) {
            clearTimeout(this.onTriggerClickTimer);
        }
        this.onTriggerClickTimer = setTimeout(() => this.toggle(), 1);
    }

    protected onConfirmButtonClicked(e: React.MouseEvent): void {
        this.toggle(false, () => this.props.onClick(e));
    }

    protected onCancelButtonClicked(e: React.MouseEvent): void {
        const onCancel = this.props.onCancel;

        this.toggle(false, onCancel ? () => onCancel(e) : undefined);
    }

    protected getGroupIdentifier(): string {
        return this.props.groupIdentifier ?? ConfirmableButton.GLOBAL_GROUP_IDENTIFIER;
    }

    protected getPopoversGroup() {
        const id = this.getGroupIdentifier();
        if (!(id in groups)) {
            groups[id] = [];
        }

        return groups[id];
    }

    protected pushPopoverToGroup(): void {
        this.getPopoversGroup().push({
            id: this.groupItemIdentifier,
            close: this.toggle.bind(this, false),
        });
    }

    protected removePopoverFromGroup(): void {
        const group = this.getPopoversGroup();
        const closerIndex = group.findIndex(item => item.id === this.groupItemIdentifier);
        group.splice(closerIndex, 1);
    }

    protected closeAnotherPopovers() {
        this.getPopoversGroup().forEach(item => {
            if (this.groupItemIdentifier !== item.id) {
                item.close();
            }
        });
    }

    protected onDocumentClicked() {
        this.toggle(false);
    }
}
